import { useEffect, useState, useRef, useMemo } from 'react';
import { useCustomFields, CustomFieldInputs } from '@/components/CustomFieldInputs';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useApp } from '@/components/AppLayout';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { useAutoSave } from '@/hooks/useAutoSave';
import { useSendBlockModal } from '@/contexts/SendBlockModalContext';
import { ClientDialog } from '@/components/ClientDialog';
import { ItemDialog } from '@/components/ItemDialog';
import { SendCreditMemoModal } from '@/components/Modals/SendCreditMemoModal';
import { AddTaxModal } from '@/components/AddTaxModal';
import { format } from 'date-fns';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Receipt,
  X,
  Plus,
  Trash2,
  User,
  CalendarIcon,
  GripVertical,
  Upload,
  File,
  Loader2,
  Check,
  Send,
} from 'lucide-react';
import {
  creditMemoApi,
  clientApi,
  itemApi,
  taxApi,
  type CreditMemo,
  type CreateCreditMemoData,
  type LineItem,
  type Client,
  type Item,
  type Tax,
} from '@/lib/api';
import { formatAmount, getCurrencySymbol } from '@/lib/currency';
import { MarkupModal } from '@/components/Modals/MarkupModal';
import type { MarkupAppliedTo } from '@/lib/api';

// ============================================================================
// SORTABLE COMPONENTS
// ============================================================================

interface SortableItemRowProps {
  item: LineItem;
  sectionIndex: number;
  itemIndex: number;
  taxes: Tax[];
  items: Item[];
  taxMode: 'line_items' | 'entire_document';
  activeItemInput: string | null;
  setActiveItemInput: (id: string | null) => void;
  setShowItemDialog: (show: boolean) => void;
  onOpenAddTaxModal: (sectionIndex: number, itemIndex: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateLineItem: (sectionIndex: number, itemIndex: number, field: keyof LineItem, value: any) => void;
  selectItemFromLibrary: (sectionIndex: number, itemIndex: number, item: Item) => void;
  removeLineItem: (sectionIndex: number, itemIndex: number) => void;
  removeTax: (sectionIndex: number, itemIndex: number, taxId: string) => void;
  addTax: (sectionIndex: number, itemIndex: number, taxId: string) => void;
}

function SortableItemRow({
  item,
  sectionIndex,
  itemIndex,
  taxes,
  items,
  taxMode,
  activeItemInput,
  setActiveItemInput,
  setShowItemDialog,
  onOpenAddTaxModal,
  updateLineItem,
  selectItemFromLibrary,
  removeLineItem,
  removeTax,
  addTax,
}: SortableItemRowProps) {
  const [taxPopoverOpen, setTaxPopoverOpen] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id! });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const inputId = `${sectionIndex}-${itemIndex}`;
  const filteredItems = items
    .filter((i) => i.name.toLowerCase().includes(item.name.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <tr ref={setNodeRef} style={style} className="border-t align-top">
      <td className="p-0 w-8 border-r">
        <div
          {...attributes}
          {...listeners}
          className="h-8 flex items-center justify-center cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        >
          <GripVertical className="h-4 w-4" />
        </div>
      </td>
      <td className="p-0 border-r relative flex flex-col">
        <Input
          placeholder="Item name..."
          value={item.name}
          onChange={(e) => updateLineItem(sectionIndex, itemIndex, 'name', e.target.value)}
          onFocus={() => setActiveItemInput(inputId)}
          onBlur={() => setTimeout(() => setActiveItemInput(null), 150)}
          className="border-0 shadow-none h-8 px-3 rounded-none flex-shrink-0"
        />
        <Textarea
          placeholder="Description..."
          value={item.description}
          onChange={(e) => updateLineItem(sectionIndex, itemIndex, 'description', e.target.value)}
          className="border-0 shadow-none px-3 py-2 rounded-none bg-green-500/5 text-sm resize-none flex-1"
          rows={3}
        />
        {activeItemInput === inputId && (
          <div className="absolute top-8 left-0 w-full mt-1 bg-popover border rounded-md shadow-lg z-50 max-h-64 overflow-auto">
            <div
              className="px-3 py-2 hover:bg-accent cursor-pointer flex items-center gap-1 text-primary border-b text-sm"
              onClick={() => {
                setActiveItemInput(null);
                setShowItemDialog(true);
              }}
            >
              <Plus className="h-3 w-3" />
              add new item to catalog
            </div>
            {filteredItems.map((libraryItem) => (
              <div
                key={libraryItem.id}
                className="px-3 py-2 hover:bg-accent cursor-pointer"
                onClick={() => selectItemFromLibrary(sectionIndex, itemIndex, libraryItem)}
              >
                <div className="text-sm">{libraryItem.name}</div>
                <div className="text-xs text-muted-foreground">
                  {formatAmount(libraryItem.rate)}
                </div>
              </div>
            ))}
          </div>
        )}
      </td>
      <td className="p-0 border-r">
        <Select
          value={item.item_type || 'product_parts'}
          onValueChange={(v) => updateLineItem(sectionIndex, itemIndex, 'item_type', v)}
        >
          <SelectTrigger className="border-0 shadow-none h-8 px-3 rounded-none text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="product_parts">Parts</SelectItem>
            <SelectItem value="labor">Labor</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td className="p-0 border-r">
        <Input
          type="number"
          min="0"
          step="0.01"
          value={item.quantity}
          onChange={(e) => updateLineItem(sectionIndex, itemIndex, 'quantity', e.target.value)}
          className="border-0 shadow-none h-8 px-3 text-right rounded-none"
        />
      </td>
      <td className="p-0 border-r">
        <div className="h-8">
          <Input
            type="number"
            step="0.01"
            value={item.rate}
            onChange={(e) => updateLineItem(sectionIndex, itemIndex, 'rate', e.target.value)}
            className="border-0 shadow-none h-8 px-3 text-right rounded-none"
          />
        </div>
        {taxMode === 'line_items' && (
          <div className="text-xs text-muted-foreground space-y-1 p-3">
            {item.tax_ids.map((taxId) => {
              const tax = taxes.find((t) => t.id === taxId);
              if (!tax) return null;
              return (
                <div key={taxId} className="flex items-center justify-end gap-1 min-h-[20px]">
                  <span>{tax.name} {tax.rate}% ({tax.tax_type})</span>
                  <button
                    type="button"
                    onClick={() => removeTax(sectionIndex, itemIndex, taxId)}
                    className="text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
            <Popover open={taxPopoverOpen} onOpenChange={setTaxPopoverOpen}>
              <PopoverTrigger asChild>
                <button type="button" className="text-primary text-xs hover:underline text-right w-full min-h-[20px]">
                  + add tax
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-1" align="end">
                <div
                  className="px-2 py-1.5 text-sm hover:bg-accent rounded cursor-pointer text-primary flex items-center gap-1"
                  onClick={() => {
                    setTaxPopoverOpen(false);
                    onOpenAddTaxModal(sectionIndex, itemIndex);
                  }}
                >
                  <Plus className="h-3 w-3" />
                  Create new
                </div>
                {taxes.filter((t) => !item.tax_ids.includes(t.id)).length > 0 && (
                  <>
                    <div className="border-t my-1" />
                    {taxes.filter((t) => !item.tax_ids.includes(t.id)).map((tax) => (
                      <div
                        key={tax.id}
                        className="px-2 py-1.5 text-sm hover:bg-accent rounded cursor-pointer"
                        onClick={() => {
                          addTax(sectionIndex, itemIndex, tax.id);
                          setTaxPopoverOpen(false);
                        }}
                      >
                        {tax.name} {tax.rate}% ({tax.tax_type})
                      </div>
                    ))}
                  </>
                )}
              </PopoverContent>
            </Popover>
          </div>
        )}
      </td>
      <td className="p-0 relative">
        <Input
          type="text"
          value={formatAmount(item.amount)}
          disabled
          className="border-0 shadow-none h-8 px-3 text-right rounded-none bg-muted/50 disabled:opacity-100 disabled:cursor-default"
        />
        <button
          type="button"
          onClick={() => removeLineItem(sectionIndex, itemIndex)}
          className="absolute right-0 top-0 translate-x-full p-2 text-muted-foreground hover:text-destructive transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
        {taxMode === 'line_items' && (
          <div className="text-xs text-muted-foreground space-y-1 px-3 py-3">
            {item.tax_ids.map((taxId) => {
              const tax = taxes.find((t) => t.id === taxId);
              if (!tax) return null;
              const taxAmount = item.amount * (parseFloat(tax.rate) / 100);
              return (
                <div key={taxId} className="min-h-[20px] flex items-center justify-end">
                  +{formatAmount(taxAmount)}
                </div>
              );
            })}
            {taxes.filter((t) => !item.tax_ids.includes(t.id)).length > 0 && (
              <div className="min-h-[20px]" />
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

interface SortableSectionProps {
  sectionId: string;
  sectionIndex: number;
  sectionName: string;
  sectionItems: LineItem[];
  lineItemsCount: number;
  taxes: Tax[];
  items: Item[];
  taxMode: 'line_items' | 'entire_document';
  activeItemInput: string | null;
  setActiveItemInput: (id: string | null) => void;
  setShowItemDialog: (show: boolean) => void;
  onOpenAddTaxModal: (sectionIndex: number, itemIndex: number) => void;
  updateSectionName: (sectionIndex: number, name: string) => void;
  removeSection: (sectionIndex: number) => void;
  addLineItem: (sectionIndex: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateLineItem: (sectionIndex: number, itemIndex: number, field: keyof LineItem, value: any) => void;
  selectItemFromLibrary: (sectionIndex: number, itemIndex: number, item: Item) => void;
  removeLineItem: (sectionIndex: number, itemIndex: number) => void;
  removeTax: (sectionIndex: number, itemIndex: number, taxId: string) => void;
  addTax: (sectionIndex: number, itemIndex: number, taxId: string) => void;
}

function SortableSection({
  sectionId,
  sectionIndex,
  sectionName,
  sectionItems,
  lineItemsCount,
  taxes,
  items,
  taxMode,
  activeItemInput,
  setActiveItemInput,
  setShowItemDialog,
  onOpenAddTaxModal,
  updateSectionName,
  removeSection,
  addLineItem,
  updateLineItem,
  selectItemFromLibrary,
  removeLineItem,
  removeTax,
  addTax,
}: SortableSectionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sectionId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="border overflow-visible rounded-lg">
        {(lineItemsCount > 1 || sectionName) && (
          <div className="relative bg-green-500/5 border-b flex items-center">
            {lineItemsCount > 1 && (
              <div
                {...attributes}
                {...listeners}
                className="h-10 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground flex-shrink-0"
              >
                <GripVertical className="h-4 w-4" />
              </div>
            )}
            <Input
              placeholder="Section name (optional)"
              value={sectionName}
              onChange={(e) => updateSectionName(sectionIndex, e.target.value)}
              className={`border-0 shadow-none h-10 px-3 rounded-none rounded-t-lg bg-transparent flex-1 ${lineItemsCount > 1 ? 'pl-0' : ''}`}
            />
            {lineItemsCount > 1 && (
              <button
                type="button"
                onClick={() => removeSection(sectionIndex)}
                className="absolute right-0 top-0 translate-x-full p-2 text-muted-foreground hover:text-destructive transition-colors h-10 flex items-center"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr className="text-xs text-muted-foreground">
              <th className="w-8"></th>
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-left px-3 py-2 w-20">Type</th>
              <th className="text-right px-3 py-2 w-20">Qty</th>
              <th className="text-right px-3 py-2 w-24">Rate</th>
              <th className="text-right px-3 py-2 w-24">Amount</th>
            </tr>
          </thead>
          <tbody>
            <SortableContext
              items={sectionItems.map((item) => item.id!)}
              strategy={verticalListSortingStrategy}
            >
              {sectionItems.map((item, itemIndex) => (
                <SortableItemRow
                  key={item.id}
                  item={item}
                  sectionIndex={sectionIndex}
                  itemIndex={itemIndex}
                  taxes={taxes}
                  items={items}
                  taxMode={taxMode}
                  activeItemInput={activeItemInput}
                  setActiveItemInput={setActiveItemInput}
                  setShowItemDialog={setShowItemDialog}
                  onOpenAddTaxModal={onOpenAddTaxModal}
                  updateLineItem={updateLineItem}
                  selectItemFromLibrary={selectItemFromLibrary}
                  removeLineItem={removeLineItem}
                  removeTax={removeTax}
                  addTax={addTax}
                />
              ))}
            </SortableContext>
          </tbody>
        </table>
        <div className="border-t px-3 py-2">
          <button
            type="button"
            onClick={() => addLineItem(sectionIndex)}
            className="text-sm text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
          >
            <Plus className="h-3 w-3" />
            Add item
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function createEmptyLineItem(): LineItem {
  return {
    id: crypto.randomUUID(),
    name: '',
    code: '',
    description: '',
    quantity: 1,
    rate: 0,
    tax_ids: [],
    amount: 0,
    item_type: 'product_parts',
  };
}

export function CreateCreditMemo() {
  const { id: creditMemoIdFromParams } = useParams<{ id: string }>();
  const { nav } = useBusinessNav();
  const { currentBusiness, user } = useApp();
  const { showEmailVerificationModal, showViewOnlyBlockModal } = useSendBlockModal();
  const businessId = currentBusiness?.id || '';

  // Phase management
  const isEditMode = !!creditMemoIdFromParams;
  // Track if we started this session creating a new document (vs editing existing)
  // This stays true even after the URL changes to include the new draft's ID
  const startedAsNew = useRef(!creditMemoIdFromParams);
  const [phase, setPhase] = useState<'client-selection' | 'full-form'>(
    creditMemoIdFromParams ? 'full-form' : 'client-selection'
  );

  // Credit memo state
  const [creditMemo, setCreditMemo] = useState<CreditMemo | null>(null);
  const [creatingCreditMemo, setCreatingCreditMemo] = useState(false);

  // Reference data
  const [clients, setClients] = useState<Client[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);

  // Form state
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [creditMemoDate, setCreditMemoDate] = useState(new Date().toISOString().split('T')[0]);
  const [lineItems, setLineItems] = useState<[string, LineItem[]][]>([
    ['', [createEmptyLineItem()]]
  ]);
  const [noteForClient, setNoteForClient] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [taxMode, setTaxMode] = useState<'line_items' | 'entire_document'>('line_items');
  const [documentTaxIds, setDocumentTaxIds] = useState<string[]>([]);
  const [markupType, setMarkupType] = useState<'dollars' | 'percent'>('percent');
  const [markupValue, setMarkupValue] = useState(0);
  const [markupAppliedTo, setMarkupAppliedTo] = useState<MarkupAppliedTo>('none');

  // Custom fields from template
  const { customFields, customFieldValues, setFieldValue, toggleField, populateValues: populateCustomFieldValues } = useCustomFields(businessId, 'credit_memo');

  // Attachments
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // UI state
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [showChangeClientDialog, setShowChangeClientDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showAddTaxModal, setShowAddTaxModal] = useState(false);
  const [addTaxTarget, setAddTaxTarget] = useState<{ sectionIndex: number; itemIndex: number } | 'document' | null>(null);

  const openAddTaxModal = (sectionIndex: number, itemIndex: number) => {
    setAddTaxTarget({ sectionIndex, itemIndex });
    setShowAddTaxModal(true);
  };
  const [showMarkupModal, setShowMarkupModal] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [savingForSend, setSavingForSend] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [discardingDraft, setDiscardingDraft] = useState(false);
  const [hasUserMadeChanges, setHasUserMadeChanges] = useState(false);
  const initialLoadComplete = useRef(false);
  const initialDataRef = useRef<string | null>(null);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [changeClientSearchQuery, setChangeClientSearchQuery] = useState('');
  const [activeItemInput, setActiveItemInput] = useState<string | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const getSectionId = (index: number) => `section-${index}`;
  const sectionIds = lineItems.map((_, index) => getSectionId(index));

  // Load reference data
  useEffect(() => {
    if (businessId) {
      clientApi.list(businessId).then((r) => r.data && setClients(r.data));
      itemApi.list(businessId).then((r) => r.data && setItems(r.data.results));
      taxApi.list(businessId).then((r) => r.data && setTaxes(r.data));
    }
  }, [businessId]);

  // Load credit memo if ID in URL
  useEffect(() => {
    if (creditMemoIdFromParams && businessId) {
      creditMemoApi.get(creditMemoIdFromParams).then((r) => {
        if (r.data) {
          populateFormFromCreditMemo(r.data);
        }
      });
    }
  }, [creditMemoIdFromParams, businessId]);

  // Set selected client when clients are loaded and credit memo is loaded
  useEffect(() => {
    if (creditMemo && clients.length > 0 && !selectedClient) {
      const client = clients.find((c) => c.id === creditMemo.client);
      if (client) {
        setSelectedClient(client);
      }
    }
  }, [creditMemo, clients, selectedClient]);

  const populateFormFromCreditMemo = (cm: CreditMemo) => {
    setCreditMemo(cm);
    setCreditMemoDate(cm.credit_memo_date || new Date().toISOString().split('T')[0]);
    setLineItems(cm.line_items.length > 0 ? cm.line_items : [['', [createEmptyLineItem()]]]);
    setNoteForClient(cm.note_for_client || '');
    setInternalNotes(cm.internal_notes || '');
    setTaxMode(cm.tax_mode || 'line_items');
    // Derive documentTaxIds from first line item when in entire_document mode
    if (cm.tax_mode === 'entire_document' && cm.line_items.length > 0) {
      const firstSection = cm.line_items[0];
      if (firstSection[1].length > 0) {
        setDocumentTaxIds(firstSection[1][0].tax_ids || []);
      }
    }
    // Load markup settings
    const markupDollars = parseFloat(cm.markup_dollars);
    const markupPercent = parseFloat(cm.markup_percent);
    if (markupDollars > 0) {
      setMarkupType('dollars');
      setMarkupValue(markupDollars);
    } else if (markupPercent > 0) {
      setMarkupType('percent');
      setMarkupValue(markupPercent);
    }
    setMarkupAppliedTo(cm.markup_applied_to || 'none');
    if (!startedAsNew.current) populateCustomFieldValues(cm.custom_field_values);
    setPhase('full-form');
  };

  // Handle client selection (Phase 1 -> Phase 2)
  const handleClientSelect = async (client: Client) => {
    if (creatingCreditMemo) return;
    setCreatingCreditMemo(true);

    const response = await creditMemoApi.create({
      business: businessId,
      client: client.id,
      credit_memo_date: creditMemoDate,
      status: 'draft',
      line_items: [['', []]],
    });

    if (response.data) {
      nav(`/credit-memos/${response.data.id}/edit`);
    }

    setCreatingCreditMemo(false);
  };

  // Handle client change (in Phase 2)
  const handleChangeClient = async (client: Client) => {
    if (!creditMemo) return;

    const response = await creditMemoApi.update(creditMemo.id, { business: businessId, client: client.id });
    if (response.data) {
      setCreditMemo(response.data);
      setSelectedClient(client);
    }
    setShowChangeClientDialog(false);
    setChangeClientSearchQuery('');
  };

  // Auto-save data
  const autoSaveData = useMemo(() => {
    if (!creditMemo?.id) return null;

    return {
      business: businessId,
      client: selectedClient?.id,
      credit_memo_date: creditMemoDate,
      line_items: lineItems,
      note_for_client: noteForClient,
      internal_notes: internalNotes,
      tax_mode: taxMode,
      markup_dollars: markupType === 'dollars' ? markupValue : 0,
      markup_percent: markupType === 'percent' ? markupValue : 0,
      markup_applied_to: markupAppliedTo,
      custom_field_values: customFieldValues,
    };
  }, [
    businessId,
    creditMemo?.id,
    selectedClient?.id,
    creditMemoDate,
    lineItems,
    noteForClient,
    internalNotes,
    taxMode,
    markupType,
    markupValue,
    markupAppliedTo,
    customFieldValues,
  ]);

  const { isSaving, lastSaved, saveNow } = useAutoSave({
    data: autoSaveData,
    onSave: async (data) => {
      if (!creditMemo?.id || !data) return;
      const response = await creditMemoApi.update(creditMemo.id, data as Partial<CreateCreditMemoData>);
      if (response.data) {
        setCreditMemo(response.data);
      }
    },
    // Only auto-save for NEW documents to avoid race condition with discard
    enabled: phase === 'full-form' && !!creditMemo?.id && startedAsNew.current,
    delay: 1500,
  });

  // Track when user makes changes (after initial load)
  useEffect(() => {
    // For new documents, track via auto-save's lastSaved
    if (startedAsNew.current && lastSaved && initialLoadComplete.current) {
      setHasUserMadeChanges(true);
    }
  }, [lastSaved]);

  // For existing documents, track changes by comparing to initial data
  useEffect(() => {
    if (startedAsNew.current || !initialLoadComplete.current || !autoSaveData) return;

    const currentData = JSON.stringify(autoSaveData);
    if (initialDataRef.current && currentData !== initialDataRef.current) {
      setHasUserMadeChanges(true);
    }
  }, [autoSaveData]);

  // Mark initial load as complete after credit memo is loaded
  useEffect(() => {
    if (creditMemo && !initialLoadComplete.current) {
      // Delay to allow form to settle before capturing initial state
      const timer = setTimeout(() => {
        initialLoadComplete.current = true;
        // Capture initial data for existing documents (for change detection)
        if (!startedAsNew.current && autoSaveData) {
          initialDataRef.current = JSON.stringify(autoSaveData);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [creditMemo, autoSaveData]);

  // Line item management
  const addLineItem = (sectionIndex: number) => {
    const newLineItems = [...lineItems];
    newLineItems[sectionIndex][1].push(createEmptyLineItem());
    setLineItems(newLineItems);
  };

  const removeLineItem = (sectionIndex: number, itemIndex: number) => {
    const newLineItems = [...lineItems];
    const sectionItems = newLineItems[sectionIndex][1].filter((_, i) => i !== itemIndex);
    if (sectionItems.length === 0) {
      sectionItems.push(createEmptyLineItem());
    }
    newLineItems[sectionIndex][1] = sectionItems;
    setLineItems(newLineItems);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateLineItem = (sectionIndex: number, itemIndex: number, field: keyof LineItem, value: any) => {
    const newLineItems = [...lineItems];
    const item = { ...newLineItems[sectionIndex][1][itemIndex] };

    if (field === 'name' || field === 'description' || field === 'code' || field === 'item_type') {
      (item[field] as string) = value;
    } else if (field === 'quantity' || field === 'rate') {
      const numValue = parseFloat(value) || 0;
      (item[field] as number) = numValue;
      item.amount = item.quantity * item.rate;
    } else if (field === 'tax_ids') {
      item.tax_ids = value;
    }

    newLineItems[sectionIndex][1][itemIndex] = item;
    setLineItems(newLineItems);
  };

  const selectItemFromLibrary = (sectionIndex: number, itemIndex: number, selectedItem: Item) => {
    const newLineItems = [...lineItems];
    const item = { ...newLineItems[sectionIndex][1][itemIndex] };

    item.name = selectedItem.name;
    item.code = selectedItem.code || '';
    item.description = selectedItem.description;
    item.quantity = parseFloat(selectedItem.default_quantity) || 1;
    item.rate = parseFloat(selectedItem.rate) || 0;
    item.tax_ids = selectedItem.taxes;
    item.amount = item.quantity * item.rate;
    item.quantity_label_text = selectedItem.quantity_label_text;
    item.item_type = selectedItem.item_type;

    newLineItems[sectionIndex][1][itemIndex] = item;
    setLineItems(newLineItems);
    setActiveItemInput(null);
  };

  const addSection = (name: string = '') => {
    setLineItems([...lineItems, [name, [createEmptyLineItem()]]]);
  };

  const updateSectionName = (sectionIndex: number, name: string) => {
    const newLineItems = [...lineItems];
    newLineItems[sectionIndex][0] = name;
    setLineItems(newLineItems);
  };

  const removeSection = (sectionIndex: number) => {
    if (lineItems.length <= 1) return;
    setLineItems(lineItems.filter((_, i) => i !== sectionIndex));
  };

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeId.startsWith('section-') && overId.startsWith('section-')) {
      return;
    }

    if (!activeId.startsWith('section-') && !overId.startsWith('section-')) {
      let activeSection = -1;
      let activeIndex = -1;
      let overSection = -1;
      let overIndex = -1;

      lineItems.forEach(([, sectionItems], sIndex) => {
        const aIndex = sectionItems.findIndex((item) => item.id === activeId);
        if (aIndex !== -1) {
          activeSection = sIndex;
          activeIndex = aIndex;
        }
        const oIndex = sectionItems.findIndex((item) => item.id === overId);
        if (oIndex !== -1) {
          overSection = sIndex;
          overIndex = oIndex;
        }
      });

      if (activeSection !== -1 && overSection !== -1 && activeSection !== overSection) {
        const newLineItems = [...lineItems];
        const [movedItem] = newLineItems[activeSection][1].splice(activeIndex, 1);
        newLineItems[overSection][1].splice(overIndex, 0, movedItem);

        if (newLineItems[activeSection][1].length === 0) {
          newLineItems[activeSection][1].push(createEmptyLineItem());
        }

        setLineItems(newLineItems);
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeId === overId) return;

    if (activeId.startsWith('section-') && overId.startsWith('section-')) {
      const oldIndex = sectionIds.indexOf(activeId);
      const newIndex = sectionIds.indexOf(overId);

      if (oldIndex !== -1 && newIndex !== -1) {
        setLineItems(arrayMove(lineItems, oldIndex, newIndex));
      }
      return;
    }

    if (!activeId.startsWith('section-') && !overId.startsWith('section-')) {
      let activeSection = -1;
      let activeIndex = -1;
      let overSection = -1;
      let overIndex = -1;

      lineItems.forEach(([, sectionItems], sIndex) => {
        const aIndex = sectionItems.findIndex((item) => item.id === activeId);
        if (aIndex !== -1) {
          activeSection = sIndex;
          activeIndex = aIndex;
        }
        const oIndex = sectionItems.findIndex((item) => item.id === overId);
        if (oIndex !== -1) {
          overSection = sIndex;
          overIndex = oIndex;
        }
      });

      if (activeSection === overSection && activeSection !== -1) {
        const newLineItems = [...lineItems];
        newLineItems[activeSection][1] = arrayMove(
          newLineItems[activeSection][1],
          activeIndex,
          overIndex
        );
        setLineItems(newLineItems);
      }
    }
  };

  // Tax management
  const removeTax = (sectionIndex: number, itemIndex: number, taxId: string) => {
    const newLineItems = [...lineItems];
    const item = { ...newLineItems[sectionIndex][1][itemIndex] };
    item.tax_ids = item.tax_ids.filter((id) => id !== taxId);
    newLineItems[sectionIndex][1][itemIndex] = item;
    setLineItems(newLineItems);
  };

  const addTax = (sectionIndex: number, itemIndex: number, taxId: string) => {
    const newLineItems = [...lineItems];
    const item = { ...newLineItems[sectionIndex][1][itemIndex] };
    if (!item.tax_ids.includes(taxId)) {
      item.tax_ids = [...item.tax_ids, taxId];
    }
    newLineItems[sectionIndex][1][itemIndex] = item;
    setLineItems(newLineItems);
  };

  // Tax mode handlers
  const handleTaxModeChange = (newMode: 'line_items' | 'entire_document') => {
    if (newMode === 'line_items' && taxMode === 'entire_document') {
      // Clear all taxes from line items when switching to line_items mode
      const clearedLineItems = lineItems.map(([name, items]) => [
        name,
        items.map(item => ({ ...item, tax_ids: [] }))
      ] as [string, LineItem[]]);
      setLineItems(clearedLineItems);
      setDocumentTaxIds([]);
    }
    setTaxMode(newMode);
  };

  const handleDocumentTaxToggle = (taxId: string, checked: boolean) => {
    let newDocTaxIds: string[];
    if (checked) {
      newDocTaxIds = [...documentTaxIds, taxId];
    } else {
      newDocTaxIds = documentTaxIds.filter(id => id !== taxId);
    }
    setDocumentTaxIds(newDocTaxIds);

    // Apply to all line items
    const updatedLineItems = lineItems.map(([name, items]) => [
      name,
      items.map(item => ({ ...item, tax_ids: newDocTaxIds }))
    ] as [string, LineItem[]]);
    setLineItems(updatedLineItems);
  };

  // Calculations (memoized to prevent stale values)
  const subtotal = useMemo(() => {
    return lineItems.reduce((total, [, items]) => {
      return total + items.reduce((sectionTotal, item) => sectionTotal + item.amount, 0);
    }, 0);
  }, [lineItems]);

  // Calculate subtotals for each item type (for markup modal and calculations)
  const applicableSubtotals = useMemo(() => {
    const subtotals = { all: 0, labor: 0, product_parts: 0, other: 0 };
    lineItems.forEach(([, items]) => {
      items.forEach((item) => {
        const itemType = item.item_type || 'product_parts';
        subtotals.all += item.amount;
        if (itemType === 'labor') subtotals.labor += item.amount;
        if (itemType === 'product_parts') subtotals.product_parts += item.amount;
        if (itemType === 'other') subtotals.other += item.amount;
      });
    });
    return subtotals;
  }, [lineItems]);

  const markup = useMemo(() => {
    if (markupAppliedTo === 'none' || markupValue <= 0) return 0;
    const applicableSubtotal = markupAppliedTo === 'all'
      ? applicableSubtotals.all
      : applicableSubtotals[markupAppliedTo] || 0;
    if (applicableSubtotal <= 0) return 0;
    if (markupType === 'dollars') return markupValue;
    return applicableSubtotal * (markupValue / 100);
  }, [markupAppliedTo, markupValue, markupType, applicableSubtotals]);

  // Note: Tax is not shown during editing - it's calculated accurately on the view page
  const total = subtotal + markup;

  // Helper for markup modal - uses memoized applicableSubtotals
  const getApplicableSubtotals = () => applicableSubtotals;

  // Get markup display text for the UI
  const getMarkupDisplayText = () => {
    if (markupAppliedTo === 'none' || markupValue <= 0) {
      return 'None';
    }
    const typeLabel = markupType === 'percent' ? `${markupValue}%` : formatAmount(markupValue);
    const targetLabel = { all: 'all items', labor: 'labor', product_parts: 'parts', other: 'other' }[markupAppliedTo];
    return `+${formatAmount(markup)} (${typeLabel} on ${targetLabel})`;
  };

  // Client filtering
  const filteredClients = clients.filter((client) =>
    client.billing_name.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
    client.company_name.toLowerCase().includes(clientSearchQuery.toLowerCase())
  );

  const filteredClientsForChange = clients.filter((client) =>
    client.billing_name.toLowerCase().includes(changeClientSearchQuery.toLowerCase()) ||
    client.company_name.toLowerCase().includes(changeClientSearchQuery.toLowerCase())
  );

  // Callbacks
  const handleClientSaved = (client?: Client) => {
    if (client) {
      setClients([...clients, client]);
      handleClientSelect(client);
    }
    setShowClientDialog(false);
  };

  const handleClientSavedForChange = (client?: Client) => {
    if (client) {
      setClients([...clients, client]);
      handleChangeClient(client);
    }
    setShowClientDialog(false);
  };

  const handleItemSaved = () => {
    itemApi.list(businessId).then((r) => r.data && setItems(r.data.results));
    setShowItemDialog(false);
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !creditMemo?.id) return;

    setUploadingAttachment(true);
    // Credit memo attachments API - placeholder for future implementation
    // For now we'll just reload the credit memo
    const response = await creditMemoApi.get(creditMemo.id);
    if (response.data) {
      setCreditMemo(response.data);
    }
    setUploadingAttachment(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRemoveAttachment = async (_index: number) => {
    if (!creditMemo?.id) return;
    // Credit memo attachments API - placeholder for future implementation
    const response = await creditMemoApi.get(creditMemo.id);
    if (response.data) {
      setCreditMemo(response.data);
    }
  };

  // Handle discard - for new documents, delete draft and go to list
  // For existing documents, just go back to detail page (changes are already auto-saved)
  const handleDiscard = async () => {
    if (!creditMemo) return;
    if (startedAsNew.current) {
      // New document created in this session - delete draft and go to list
      setDiscardingDraft(true);
      try {
        await creditMemoApi.delete(creditMemo.id);
        nav('/credit-memos');
      } catch (error) {
        console.error('Failed to delete draft:', error);
        setDiscardingDraft(false);
        setShowDiscardDialog(false);
      }
    } else {
      // Editing existing document - just go back to detail page
      nav(`/credit-memos/${creditMemo.id}`);
    }
  };

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <>
      <div className="fixed inset-0 z-50 bg-background flex flex-col overflow-hidden">
        {/* Main content */}
        <div className="flex-1 flex overflow-hidden">
          <AnimatePresence mode="wait">
            {phase === 'client-selection' ? (
            <motion.div
              key="client-selection"
              initial={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center gap-3 px-6 py-4 flex-shrink-0">
                <button
                  onClick={() => nav('/credit-memos')}
                  className="p-2 -ml-2 rounded-md hover:bg-muted transition-colors"
                  title="Cancel"
                >
                  <X className="h-5 w-5" />
                </button>
                <img src="/owl.svg" alt="InvoiceOwl" className="h-8 w-auto" />
                <div className="h-6 w-px bg-border" />
                <span className="font-medium text-sm">{currentBusiness?.name}</span>
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="flex flex-col items-center px-4 pt-12 pb-16">
                <div className="w-full max-w-md">
                  <h1 className="text-2xl mb-8 flex items-center gap-2">
                    {isEditMode ? 'Edit Credit Memo' : 'New Credit Memo'}
                    <Receipt className="h-5 w-5 text-green-500" />
                  </h1>

                  <div className="space-y-5">
                    <div className="space-y-2 group">
                      <Label className="text-muted-foreground group-focus-within:text-foreground transition-colors">
                        Select a client
                      </Label>
                      <Input
                        placeholder="Search clients..."
                        value={clientSearchQuery}
                        onChange={(e) => setClientSearchQuery(e.target.value)}
                        autoFocus
                      />
                    </div>

                    {clients.length === 0 ? (
                      <div className="py-8 text-center">
                        <p className="text-muted-foreground mb-4">No clients yet</p>
                        <Button
                          variant="outline"
                          onClick={() => setShowClientDialog(true)}
                          disabled={creatingCreditMemo}
                          className="rounded-full"
                        >
                          + Add your first client
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="border rounded-lg divide-y max-h-[320px] overflow-y-auto">
                          {filteredClients.length === 0 ? (
                            <div className="py-6 text-center text-muted-foreground text-sm">
                              No clients match your search
                            </div>
                          ) : (
                            filteredClients.map((client) => (
                              <div
                                key={client.id}
                                className={`px-4 py-3 cursor-pointer transition-colors ${creatingCreditMemo ? 'opacity-50' : 'hover:bg-muted/50'}`}
                                onClick={() => !creatingCreditMemo && handleClientSelect(client)}
                              >
                                <div className="text-sm">{client.billing_name}</div>
                                {client.company_name && (
                                  <div className="text-xs text-muted-foreground">{client.company_name}</div>
                                )}
                              </div>
                            ))
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => setShowClientDialog(true)}
                          disabled={creatingCreditMemo}
                          className="text-sm text-primary hover:underline disabled:opacity-50"
                        >
                          + Add new client
                        </button>
                      </>
                    )}

                    <div className="pt-4">
                      <Button
                        variant="outline"
                        onClick={() => nav('/credit-memos')}
                        className="w-full rounded-full"
                      >
                        Go Back
                      </Button>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="full-form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center gap-3 px-6 py-4 flex-shrink-0">
                <button
                  onClick={() => {
                    // Show modal if: new document in this session, OR user made changes
                    if (startedAsNew.current || hasUserMadeChanges) {
                      setShowDiscardDialog(true);
                    } else {
                      nav(`/credit-memos/${creditMemo?.id}`);
                    }
                  }}
                  className="p-2 -ml-2 rounded-md hover:bg-muted transition-colors"
                  title="Cancel"
                >
                  <X className="h-5 w-5" />
                </button>
                <img src="/owl.svg" alt="InvoiceOwl" className="h-8 w-auto" />
                <div className="h-6 w-px bg-border" />
                <span className="font-medium text-sm">{currentBusiness?.name}</span>
              </div>

              <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-8 pb-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="max-w-3xl mx-auto space-y-6"
                >
                  <h1 className="text-2xl flex items-center gap-2">
                    {isEditMode ? 'Edit Credit Memo' : 'New Credit Memo'}
                    <Receipt className="h-5 w-5 text-green-500" />
                  </h1>

                  {/* Client Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="space-y-2"
                  >
                    <Label className="text-muted-foreground">Client</Label>
                    {selectedClient && (
                      <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p>{selectedClient.billing_name}</p>
                            {selectedClient.company_name && (
                              <p className="text-sm text-muted-foreground">{selectedClient.company_name}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowChangeClientDialog(true)}
                        >
                          Change
                        </Button>
                      </div>
                    )}
                  </motion.div>

                  {/* Credit Memo Date */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="space-y-2"
                  >
                    <Label className="text-muted-foreground">Credit Memo Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full max-w-xs justify-start text-left font-normal bg-green-500/5"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {creditMemoDate ? format(new Date(creditMemoDate + 'T00:00:00'), 'PPP') : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={creditMemoDate ? new Date(creditMemoDate + 'T00:00:00') : undefined}
                          onSelect={(date) => date && setCreditMemoDate(date.toISOString().split('T')[0])}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </motion.div>

                  {/* Custom Fields */}
                  {customFields.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.22 }}
                    >
                      <CustomFieldInputs
                        customFields={customFields}
                        customFieldValues={customFieldValues}
                        setFieldValue={setFieldValue}
                        toggleField={toggleField}
                      />
                    </motion.div>
                  )}

                  {/* Line Items */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="space-y-4"
                  >
                    <Label className="text-muted-foreground">Line Items</Label>

                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
                        <div className="space-y-4">
                          {lineItems.map(([sectionName, sectionItems], sectionIndex) => (
                            <SortableSection
                              key={getSectionId(sectionIndex)}
                              sectionId={getSectionId(sectionIndex)}
                              sectionIndex={sectionIndex}
                              sectionName={sectionName}
                              sectionItems={sectionItems}
                              lineItemsCount={lineItems.length}
                              taxes={taxes}
                              items={items}
                              taxMode={taxMode}
                              activeItemInput={activeItemInput}
                              setActiveItemInput={setActiveItemInput}
                              setShowItemDialog={setShowItemDialog}
                              onOpenAddTaxModal={openAddTaxModal}
                              updateSectionName={updateSectionName}
                              removeSection={removeSection}
                              addLineItem={addLineItem}
                              updateLineItem={updateLineItem}
                              selectItemFromLibrary={selectItemFromLibrary}
                              removeLineItem={removeLineItem}
                              removeTax={removeTax}
                              addTax={addTax}
                            />
                          ))}
                        </div>
                      </SortableContext>
                      <DragOverlay>
                        {activeDragId && !activeDragId.startsWith('section-') && (
                          <div className="bg-background border rounded shadow-lg p-2 text-sm">
                            Dragging item...
                          </div>
                        )}
                        {activeDragId && activeDragId.startsWith('section-') && (
                          <div className="bg-background border rounded shadow-lg p-2 text-sm">
                            Dragging section...
                          </div>
                        )}
                      </DragOverlay>
                    </DndContext>

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addSection()}
                        className="text-sm"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add section
                      </Button>
                    </div>
                  </motion.div>

                  {/* Totals */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="border-t pt-4 space-y-2"
                  >
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>{formatAmount(subtotal)}</span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Markup</span>
                      <button
                        type="button"
                        onClick={() => setShowMarkupModal(true)}
                        className="text-green-600 hover:text-green-700 hover:underline text-sm font-medium"
                      >
                        {getMarkupDisplayText()}
                      </button>
                    </div>

                    <div className="flex justify-between text-lg pt-2 border-t">
                      <span>Credit Total</span>
                      <span className="text-green-600">{formatAmount(total)}</span>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

          {/* Right: Fixed Sidebar */}
          {phase === 'full-form' && (
            <motion.div
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2, type: 'spring', damping: 25 }}
              className="w-72 border-l bg-muted/30 flex flex-col flex-shrink-0"
            >
              {/* Save status */}
              <div className="px-5 py-4 flex items-center justify-end">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : lastSaved ? (
                    <>
                      <Check className="h-4 w-4 text-green-500" />
                      <span>Saved</span>
                    </>
                  ) : null}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 pb-5 space-y-5">
                <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Settings</h2>

                  {/* Tax Mode Toggle */}
                  <div className="space-y-3">
                    <Label className="text-muted-foreground">Tax Application</Label>
                    <div className="flex gap-2">
                      <Button
                        variant={taxMode === 'line_items' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleTaxModeChange('line_items')}
                      >
                        Per Line Item
                      </Button>
                      <Button
                        variant={taxMode === 'entire_document' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleTaxModeChange('entire_document')}
                      >
                        Entire Document
                      </Button>
                    </div>
                  </div>

                  {/* Document-level tax selection (when entire_document mode) */}
                  {taxMode === 'entire_document' && (
                    <div className="space-y-3">
                      <Label className="text-muted-foreground">Document Taxes</Label>
                      <div className="space-y-2">
                        {taxes.map((tax) => (
                          <div key={tax.id} className="flex items-center gap-2">
                            <Checkbox
                              id={`doc-tax-${tax.id}`}
                              checked={documentTaxIds.includes(tax.id)}
                              onCheckedChange={(checked) => handleDocumentTaxToggle(tax.id, !!checked)}
                            />
                            <label htmlFor={`doc-tax-${tax.id}`} className="text-sm cursor-pointer">
                              {tax.name} ({tax.rate}%, {tax.tax_type})
                            </label>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="text-primary text-sm hover:underline flex items-center gap-1"
                          onClick={() => {
                            setAddTaxTarget('document');
                            setShowAddTaxModal(true);
                          }}
                        >
                          <Plus className="h-3 w-3" />
                          Create new
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Attachments */}
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Attachments</Label>

                    {creditMemo?.id && Array.isArray(creditMemo.attachments) && creditMemo.attachments.length > 0 ? (
                      <div className="space-y-2">
                        {creditMemo.attachments.map((att, index) => (
                          <div key={`${creditMemo.id}-${att.url || index}`} className="flex items-center justify-between p-2 border rounded-lg bg-background">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <File className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <span className="text-xs truncate">{att.filename}</span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 flex-shrink-0"
                              onClick={() => handleRemoveAttachment(index)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    <div
                      className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors cursor-pointer bg-background ${uploadingAttachment ? 'opacity-50' : 'hover:border-primary'}`}
                      onClick={() => !uploadingAttachment && fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => handleFileUpload(e.target.files)}
                        accept="image/*,.pdf,.doc,.docx"
                      />
                      {uploadingAttachment ? (
                        <Loader2 className="h-4 w-4 text-muted-foreground mx-auto mb-1 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 text-muted-foreground mx-auto mb-1" />
                      )}
                      <p className="text-xs text-muted-foreground">
                        {uploadingAttachment ? 'Uploading...' : 'Click to upload'}
                      </p>
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Note for Client</Label>
                    <Textarea
                      placeholder="Visible to the client..."
                      value={noteForClient}
                      onChange={(e) => setNoteForClient(e.target.value)}
                      rows={3}
                      className="resize-none bg-background"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Internal Notes</Label>
                    <Textarea
                      placeholder="Not visible to client..."
                      value={internalNotes}
                      onChange={(e) => setInternalNotes(e.target.value)}
                      rows={3}
                      className="resize-none bg-background"
                    />
                  </div>
                </div>

                {/* Fixed action buttons at bottom */}
                <div className="p-4 border-t bg-background space-y-3">
                  {creditMemo?.status === 'draft' && (
                    <Button
                      type="button"
                      onClick={async () => {
                        if (!user?.is_email_verified) {
                          showEmailVerificationModal(user?.email || '');
                          return;
                        }
                        setSavingForSend(true);
                        const response = await creditMemoApi.update(creditMemo.id, autoSaveData as Partial<CreateCreditMemoData>);
                        setSavingForSend(false);
                        if (!response.data) return;
                        setCreditMemo(response.data);
                        setSendModalOpen(true);
                      }}
                      disabled={!creditMemo || savingForSend}
                      className="w-full"
                    >
                      {savingForSend ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-1" />
                      )}
                      {savingForSend ? 'Saving...' : 'Send'}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      if (!creditMemo) return;
                      await saveNow();
                      nav(`/credit-memos/${creditMemo.id}`);
                    }}
                    disabled={!creditMemo}
                    className="w-full"
                  >
                    Done
                  </Button>
                </div>
              </motion.div>
          )}
        </div>
      </div>

      {/* Client Dialog for creating new client (Phase 1) */}
      {businessId && (
        <ClientDialog
          open={showClientDialog}
          onClose={() => setShowClientDialog(false)}
          businessId={businessId}
          onSaved={showChangeClientDialog ? handleClientSavedForChange : handleClientSaved}
        />
      )}

      {/* Change Client Dialog (Phase 2) */}
      {showChangeClientDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-lg shadow-lg w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold">Change Client</h2>
              <p className="text-sm text-muted-foreground">Select a different client for this credit memo.</p>
            </div>

            <div className="p-4 space-y-4 flex-1 overflow-hidden flex flex-col">
              <div className="flex gap-2">
                <Input
                  placeholder="Search clients..."
                  value={changeClientSearchQuery}
                  onChange={(e) => setChangeClientSearchQuery(e.target.value)}
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowChangeClientDialog(false);
                    setShowClientDialog(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  New
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto space-y-2">
                {filteredClientsForChange.map((client) => (
                  <div
                    key={client.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      client.id === selectedClient?.id
                        ? 'bg-primary/10 border-primary'
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => handleChangeClient(client)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                        <User className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{client.billing_name}</p>
                        {client.company_name && (
                          <p className="text-xs text-muted-foreground">{client.company_name}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowChangeClientDialog(false);
                  setChangeClientSearchQuery('');
                }}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Item Dialog */}
      {businessId && (
        <ItemDialog
          open={showItemDialog}
          onClose={() => setShowItemDialog(false)}
          businessId={businessId}
          onSaved={handleItemSaved}
        />
      )}

      {/* Add Tax Modal */}
      {businessId && (
        <AddTaxModal
          open={showAddTaxModal}
          onClose={() => {
            setShowAddTaxModal(false);
            setAddTaxTarget(null);
          }}
          businessId={businessId}
          onTaxCreated={(taxId) => {
            taxApi.list(businessId).then((r) => r.data && setTaxes(r.data));
            if (addTaxTarget && addTaxTarget !== 'document') {
              addTax(addTaxTarget.sectionIndex, addTaxTarget.itemIndex, taxId);
            } else if (addTaxTarget === 'document') {
              handleDocumentTaxToggle(taxId, true);
            }
            setAddTaxTarget(null);
          }}
        />
      )}

      {/* Send Credit Memo Modal */}
      {creditMemo && (
        <SendCreditMemoModal
          open={sendModalOpen}
          onOpenChange={setSendModalOpen}
          creditMemo={creditMemo}
          client={selectedClient || null}
          business={currentBusiness}
          onSend={async (data) => {
            const response = await creditMemoApi.send(creditMemo.id, {
              medium: 'email',
              destination: data.to.join(','),
              message: data.message,
              cc_self: data.cc_self,
            });
            if (response.sendBlocked) {
              setSendModalOpen(false);
              if (response.sendBlocked.code === 'email_not_verified') {
                showEmailVerificationModal(response.sendBlocked.email || '');
              } else {
                showViewOnlyBlockModal();
              }
              return;
            }
            if (response.data) {
              setCreditMemo(response.data.credit_memo);
            }
            setSendModalOpen(false);
            nav(`/credit-memos/${creditMemo.id}`);
          }}
        />
      )}

      {/* Markup Modal */}
      <MarkupModal
        open={showMarkupModal}
        onOpenChange={setShowMarkupModal}
        markupType={markupType}
        markupValue={markupValue}
        markupAppliedTo={markupAppliedTo}
        applicableSubtotals={getApplicableSubtotals()}
        onSave={(type, value, appliedTo) => {
          setMarkupType(type);
          setMarkupValue(value);
          setMarkupAppliedTo(appliedTo);
        }}
        currencySymbol={getCurrencySymbol()}
        formatAmount={formatAmount}
      />

      {/* Cancel Draft Dialog */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <button
            onClick={() => setShowDiscardDialog(false)}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
            disabled={discardingDraft}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
          <AlertDialogHeader>
            <AlertDialogTitle>Save or Discard?</AlertDialogTitle>
            <AlertDialogDescription>
              {startedAsNew.current
                ? 'Would you like to save this credit memo as a draft or discard it?'
                : 'Would you like to save your changes or discard them?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={async () => {
                await saveNow();
                // New document → list, existing → detail page
                if (isEditMode && creditMemo) {
                  nav(`/credit-memos/${creditMemo.id}`);
                } else {
                  nav('/credit-memos');
                }
              }}
              disabled={discardingDraft}
            >
              {startedAsNew.current ? 'Save Draft' : 'Save Changes'}
            </AlertDialogAction>
            <AlertDialogAction
              onClick={handleDiscard}
              disabled={discardingDraft}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {discardingDraft ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Discarding...
                </>
              ) : (
                'Discard'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
